import { useMemo, useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../../../hooks/common/common_fields/useLanguage';
import { Container } from '../../blocks';
import Icon, { iconNames } from '../../icon';
import { onButtonKey } from '../../../../utils/common/a11y';

const LanguageField = () => {
    const { languages, onChange, currentLanguage } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const langConfig = useMemo(
        () => ({
            iconMap: {
                en: iconNames.eng,
                ua: iconNames.ua,
                ru: iconNames.rus,
                ge: iconNames.geo,
            },
            nameMap: {
                en: 'English',
                ua: 'Українська',
                ru: 'Русский',
                ge: 'ქართული',
            },
        }),
        [],
    );

    const getCurrentIcon = useMemo(
        () => langConfig.iconMap[currentLanguage] || iconNames.eng,
        [langConfig.iconMap, currentLanguage],
    );

    const getLanguageName = useMemo(
        () => (lang) => langConfig.nameMap[lang] || 'English',
        [langConfig.nameMap],
    );

    useEffect(() => {
        const handler = (e) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSelect = (lang) => {
        onChange({ target: { value: lang } });
        setIsOpen(false);
    };

    const isLoading = !languages || !currentLanguage;

    if (isLoading) {
        return (
            <Container>
                <span style={{ fontSize: '14px', color: '#eaeaea' }}>...</span>
            </Container>
        );
    }

    return (
        <Container>
            <div className="lang-dropdown" ref={dropdownRef}>
                {/* Trigger */}
                <button
                    className={`lang-dropdown__trigger ${isOpen ? 'lang-dropdown__trigger--open' : ''}`}
                    onClick={() => setIsOpen((v) => !v)}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    type="button"
                >
                    <span className="lang-dropdown__flag">
                        <Icon name={getCurrentIcon} size={16} />
                    </span>
                    <span className="lang-dropdown__current">
                        {getLanguageName(currentLanguage)}
                    </span>
                    <svg
                        className={`lang-dropdown__chevron ${isOpen ? 'lang-dropdown__chevron--up' : ''}`}
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        fill="none"
                    >
                        <path
                            d="M2 3.5L5 6.5L8 3.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

                {/* Dropdown */}
                {isOpen && (
                    <ul className="lang-dropdown__menu" role="listbox">
                        {languages.map((lang) => (
                            <li
                                key={lang}
                                className={`lang-dropdown__option ${lang === currentLanguage ? 'lang-dropdown__option--active' : ''}`}
                                role="option"
                                aria-selected={lang === currentLanguage}
                                tabIndex={0}
                                onClick={() => handleSelect(lang)}
                                onKeyDown={onButtonKey(() =>
                                    handleSelect(lang),
                                )}
                            >
                                <span className="lang-dropdown__flag">
                                    <Icon
                                        name={
                                            langConfig.iconMap[lang] ||
                                            iconNames.eng
                                        }
                                        size={16}
                                    />
                                </span>
                                <span className="lang-dropdown__name">
                                    {getLanguageName(lang)}
                                </span>
                                {lang === currentLanguage && (
                                    <svg
                                        className="lang-dropdown__check"
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                    >
                                        <path
                                            d="M2 6L5 9L10 3"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Container>
    );
};

export default LanguageField;
